import { useRef, useState } from "react";
import { accountService, homeService, taskService } from "@miplatform/api";
import {
  CONTACT_TYPES,
  ENTITY_TYPES,
  HTTP_STATUSES,
  MAX_PAGE_SIZE,
  ORDER_TYPES,
} from "@miplatform/constants";
import { useComponentState, useMaps } from "@miplatform/hooks";
import { AccountType } from "@miplatform/types";
import { createUUID, externalReferenceId } from "@miplatform/utils";
import { useDocumentsViewModel } from "@miplatform/view-models";
import { DEFAULT_FILE_CATEGORY } from "./addHomeConstants";

const GET_COMMUNITIES_UUID = createUUID();
const GET_BUILDERS_UUID = createUUID();
const GET_CONTACT_CATEGORIES_UUID = createUUID();
const GET_ORDER_TEMPLATES_UUID = createUUID();

export const useAddHomeFormViewModel = () => {
  const { loading, uploadDocument, getDocumentCategories } =
    useDocumentsViewModel();
  const documentCategoryRef = useRef(null);
  const [builders, setBuilders] = useState([]);
  const [communities, setCommunities] = useState([]);
  const [contactCategories, setContactCategories] = useState([]);
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [allowedFileFormats, setAllowedFileFormats] = useState([]);
  const [orderTemplateOptions, setOrderTemplateOptions] = useState([]);

  const { searchAddresses, getLatLng, getZip } = useMaps();

  const stateManager = useComponentState();

  const createContact = async ({ accountId, data }) => {
    return stateManager.run(async () => {
      const { data: contact, status } = await accountService.contacts.create({
        accountId,
        data: {
          ...data,
          accountId,
        },
        toastMessageConfig: {
          success: {
            text: "Contact was created successfully.",
          },
        },
      });

      if (status === HTTP_STATUSES.CREATED) {
        return contact.id;
      }

      return "";
    });
  };

  const createHome = async ({ data }) => {
    return stateManager.run(async () => {
      const { status, data: home } = await homeService.homes.create({
        data,
        toastMessageConfig: {
          success: {
            text: `${data.address.addressLine1} was created.`,
          },
        },
      });

      if (status === HTTP_STATUSES.OK) {
        return home;
      }

      return null;
    });
  };

  const createAccount = async (data) => {
    return stateManager.run(async () => {
      const { data: account, status } =
        await accountService.accounts.createAccount({
          data,
          toastMessageConfig: {
            success: {
              text: "Account was created successfully.",
            },
          },
        });

      if (status === HTTP_STATUSES.CREATED) {
        return account;
      }

      return null;
    });
  };

  const getContactCategories = () => {
    stateManager.abort(GET_CONTACT_CATEGORIES_UUID);
    stateManager.run(async () => {
      const { status, data } = await accountService.contactCategories.getByType(
        {
          type: CONTACT_TYPES.HOMES,
        }
      );
      if (status === HTTP_STATUSES.OK) {
        setContactCategories(
          data
            .filter(({ isActive }) => isActive)
            .map(({ label }) => ({
              value: label,
              label,
            }))
        );
      }
    }, GET_CONTACT_CATEGORIES_UUID);
  };

  const getDocumentCategoryOptions = async () => {
    const categories = await getDocumentCategories(ENTITY_TYPES.HOME);
    documentCategoryRef.current =
      categories.find((category) => category.name === DEFAULT_FILE_CATEGORY) ||
      categories[0];
    setAllowedFileFormats(
      documentCategoryRef.current?.allowedFileFormats || []
    );
  };

  const getBuilders = () => {
    stateManager.abort(GET_BUILDERS_UUID);
    stateManager.run(async () => {
      const response = await accountService.accounts.getAll({
        top: MAX_PAGE_SIZE,
        sort: "name",
        filters: { accountType: AccountType.Builder },
      });
      if (response.status === HTTP_STATUSES.OK) {
        setBuilders(
          response.data.map((builderResponse) => ({
            label: builderResponse.name,
            value: builderResponse.id,
            ...builderResponse,
          }))
        );
      }
    }, GET_BUILDERS_UUID);
  };

  const getCommunities = (builderId = null) => {
    stateManager.abort(GET_COMMUNITIES_UUID);
    stateManager.run(async () => {
      const { status, data } = await homeService.communities.getAll({
        sort: "name",
        ...(builderId && {
          filters: {
            builder: { id: { type: "guid", value: builderId } },
          },
        }),
      });

      if (status === HTTP_STATUSES.OK) {
        setCommunities(data);
      }
    }, GET_COMMUNITIES_UUID);
  };

  const getOrderTemplates = () => {
    stateManager.abort(GET_ORDER_TEMPLATES_UUID);
    stateManager.run(async () => {
      const response = await taskService.orderTemplates.getAll({
        filters: {
          homePhases: { any: ORDER_TYPES.UNDER_CONSTRUCTION },
          fulfillmentSequence: { ne: null },
        },
      });
      if (response.status === HTTP_STATUSES.OK) {
        setOrderTemplateOptions(
          response.data
            .sort((a, b) => a.fulfillmentSequence - b.fulfillmentSequence)
            .map(({ id, name, shortName, color }) => ({
              value: id,
              label: name,
              shortName,
              backgroundColor: color,
            }))
        );
      }
    }, GET_ORDER_TEMPLATES_UUID);
  };

  const getAddressesSuggestions = async (text) => {
    if (text) {
      const results = await searchAddresses(text);

      setAddressSuggestions(
        results.map((address) => ({
          value: address,
          label: address,
        }))
      );
    }
  };

  const addContactRelationship = ({
    accountId,
    contactId,
    homeId,
    label,
    isPrimary,
  }) => {
    return stateManager.run(async () => {
      await accountService.contacts.createContactRelationship({
        id: contactId,
        data: {
          accountId,
          label,
          isPrimary,
          relatedReferenceId: externalReferenceId.getHomeRefId({ homeId }),
        },
        toastMessageConfig: {
          success: {
            text: "Contact relationship created successfully.",
          },
        },
      });
    });
  };

  return {
    builders,
    communities,
    addressSuggestions,
    contactCategories,
    orderTemplateOptions,
    documentCategory: documentCategoryRef.current,
    allowedFileFormats,
    isLoading: stateManager.isBusy() || loading,
    getLatLng,
    uploadDocument,
    createAccount,
    createContact,
    createHome,
    addContactRelationship,
    getContactCategories,
    getBuilders,
    getCommunities,
    getOrderTemplates,
    getDocumentCategoryOptions,
    getAddressesSuggestions,
    getZip,
  };
};
